// workAreas/styled.js
import { styled } from '@mui/material/styles';
import { Box, Typography, Button, Paper, Alert, Fab } from '@mui/material';

// Import the existing honey colors
const honeyGeneralColors = {
  honeyBrown: '#8B4513',
  honeycombGradient: 'linear-gradient(135deg, #F1C40F 0%, #F39C12 100%)',
  honeyGradient: 'linear-gradient(135deg, #FFF8DC 0%, #F5DEB3 100%)',
  softCream: '#FFFEF7',
  darkHoney: '#CD853F',
  honeycomb: '#DAA520'
};

export const StyledContainer = styled(Box)(({ theme }) => ({
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  color: honeyGeneralColors.honeyBrown,
  maxWidth: '800px',
  margin: '0 auto',
  padding: theme.spacing(3, 0),
}));

export const StyledHeader = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(3),
}));

export const StyledTitle = styled(Typography)(({ theme }) => ({
  fontSize: '2.2rem',
  fontWeight: 700,
  marginBottom: theme.spacing(0.5),
  background: honeyGeneralColors.honeycombGradient,
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  color: 'transparent',
}));

export const StyledSectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.2rem',
  fontWeight: 600,
  color: honeyGeneralColors.honeyBrown,
  marginBottom: theme.spacing(2),
}));

export const StyledAddButton = styled(Button)(({ theme }) => ({
  background: honeyGeneralColors.honeyGradient,
  color: honeyGeneralColors.honeyBrown,
  fontWeight: 600,
  borderRadius: 8,
  boxShadow: '0 2px 8px rgba(241,196,15,0.08)',
  transition: theme.transitions.create(['background', 'color', 'box-shadow'], {
    duration: theme.transitions.duration.short,
  }),
  '&:hover': {
    background: honeyGeneralColors.honeycombGradient,
    color: honeyGeneralColors.darkHoney,
    boxShadow: '0 4px 16px rgba(241,196,15,0.16)',
  },
  '&:disabled': {
    opacity: 0.6,
  },
}));

export const StyledWorkAreaPaper = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'isExpanded',
})(({ theme, isExpanded }) => ({
  marginBottom: theme.spacing(3),
  overflow: 'hidden',
  transition: theme.transitions.create(['box-shadow', 'transform'], {
    duration: theme.transitions.duration.short,
  }),
  background: honeyGeneralColors.softCream,
  borderRadius: 12,
  boxShadow: isExpanded 
    ? '0 8px 24px rgba(212,165,116,0.16)'
    : '0 2px 8px rgba(212,165,116,0.08)',
  '&:hover': {
    boxShadow: '0 4px 16px rgba(212,165,116,0.12)',
  },
}));

export const StyledWorkAreaHeader = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isExpanded',
})(({ theme, isExpanded }) => ({
  padding: theme.spacing(2),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  cursor: 'pointer',
  backgroundColor: isExpanded ? theme.palette.primary.light : 'transparent',
  color: isExpanded ? theme.palette.primary.contrastText : 'inherit',
  transition: theme.transitions.create(['background-color', 'color'], {
    duration: theme.transitions.duration.short,
  }),
  '&:hover': {
    backgroundColor: isExpanded 
      ? theme.palette.primary.light 
      : theme.palette.action.hover,
  },
}));

export const StyledWorkAreaDetails = styled(Box)(({ theme }) => ({
  '& .MuiDivider-root': {
    margin: theme.spacing(3, 0),
    background: honeyGeneralColors.honeycomb,
  },
}));

export const StyledSectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  '& .MuiTypography-h6': {
    fontWeight: 600,
  },
}));

export const StyledSectionContent = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

// NEW: Floating Save Button Components
export const StyledFloatingSaveContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(3),
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 1000,
}));

export const StyledFloatingSaveWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  backgroundColor: honeyGeneralColors.softCream,
  padding: theme.spacing(2),
  borderRadius: theme.spacing(3),
  boxShadow: '0 8px 24px rgba(212,165,116,0.16)',
  border: `1px solid ${honeyGeneralColors.honeycomb}`,
}));

export const StyledFloatingSaveCancelButton = styled(Button)(({ theme }) => ({
  minWidth: 100,
  color: honeyGeneralColors.honeyBrown,
  borderColor: honeyGeneralColors.honeyBrown,
  '&:hover': {
    borderColor: honeyGeneralColors.darkHoney,
    backgroundColor: 'rgba(139, 69, 19, 0.04)',
    transform: 'scale(1.02)',
  },
  transition: 'all 0.2s ease-in-out',
}));

export const StyledFloatingSaveButton = styled(Button)(({ theme }) => ({
  minWidth: 150,
  fontWeight: 700,
  fontSize: '1.1rem',
  padding: theme.spacing(1.5, 3),
  background: honeyGeneralColors.honeycombGradient,
  color: 'white',
  borderRadius: theme.spacing(1),
  boxShadow: '0 8px 24px rgba(241,196,15,0.3)',
  '&:hover': {
    background: honeyGeneralColors.honeycombGradient,
    boxShadow: '0 12px 32px rgba(241,196,15,0.4)',
    transform: 'scale(1.02)',
  },
  '&:disabled': {
    opacity: 0.8,
    background: honeyGeneralColors.honeycombGradient,
    color: 'white',
  },
  transition: 'all 0.2s ease-in-out',
}));

// Keep existing styled components
export const StyledLoadingContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  textAlign: 'center',
  '& .MuiCircularProgress-root': {
    color: honeyGeneralColors.honeycomb,
  },
  '& .MuiTypography-h6': {
    color: honeyGeneralColors.honeyBrown,
    fontWeight: 500,
  },
}));

export const StyledEmptyState = styled(Alert)(({ theme }) => ({
  borderRadius: 12,
  '& .MuiAlert-message': {
    fontSize: '1rem',
    fontWeight: 500,
  },
}));

export const StyledHeaderSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
  '& .MuiTypography-h5': {
    fontWeight: 600,
    color: honeyGeneralColors.honeyBrown,
  },
  '& .MuiSvgIcon-root': {
    fontSize: '2rem',
  },
}));

export const StyledWorkAreaInfo = styled(Box)(({ theme }) => ({
  '& .MuiTypography-h6': {
    fontWeight: 600,
    color: honeyGeneralColors.honeyBrown,
  },
  '& .MuiTypography-body2': {
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
}));

export const StyledExpandIcon = styled(Box)(({ theme }) => ({
  '& .MuiIconButton-root': {
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.short,
    }),
    color: 'inherit',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
}));

// Legacy components (kept for backwards compatibility but hidden)
export const StyledSaveButtonContainer = styled(Box)(({ theme }) => ({
  display: 'none',
  justifyContent: 'flex-end',
  marginTop: theme.spacing(3),
  '& .MuiButton-root': {
    minWidth: theme.spacing(20),
    fontWeight: 600,
    borderRadius: 8,
    padding: theme.spacing(1.5, 3),
    background: honeyGeneralColors.honeycombGradient,
    color: 'white',
    '&:hover': {
      background: honeyGeneralColors.honeycombGradient,
      opacity: 0.9,
    },
    '&:disabled': {
      opacity: 0.6,
      color: theme.palette.text.disabled,
      background: theme.palette.action.disabledBackground,
    },
  },
}));